import { useEffect, useRef } from 'react';

const HANDLE_SIZE = 12;
const EDGE_HANDLE_LONG = 28;
const EDGE_HANDLE_SHORT = 6;
const MIN_SIZE = 50;
const BRAND = '#e9562b';
const DIM_COLOR = 'rgba(15, 23, 42, 0.5)';

const getPointer = (e) => {
  const touch = e.changedTouches?.[0] || e.touches?.[0];
  if (touch) {
    return { x: touch.clientX, y: touch.clientY };
  }
  return { x: e.clientX, y: e.clientY };
};

const cornerHandleStyle = (top, left, cursor) => ({
  position: 'fixed',
  width: `${HANDLE_SIZE}px`,
  height: `${HANDLE_SIZE}px`,
  background: '#fff',
  border: `2px solid ${BRAND}`,
  borderRadius: '50%',
  cursor,
  zIndex: 100001,
  top: `${top}px`,
  left: `${left}px`,
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'all',
  transition: 'background 0.15s, transform 0.12s, box-shadow 0.12s',
  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
});

const edgeHandleStyle = (top, left, width, height, cursor) => ({
  position: 'fixed',
  width: `${width}px`,
  height: `${height}px`,
  background: '#fff',
  border: `2px solid ${BRAND}`,
  borderRadius: '3px',
  cursor,
  zIndex: 100001,
  top: `${top}px`,
  left: `${left}px`,
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'all',
  transition: 'background 0.15s, transform 0.12s',
  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
});

const computeSize = ({
  startW,
  startH,
  aspectRatio,
  deltaX,
  deltaY,
  dx,
  dy,
  lockAspect,
}) => {
  let newW = startW;
  let newH = startH;

  if (dx !== 0) {
    newW = Math.max(MIN_SIZE, startW + deltaX * dx);
  }
  if (dy !== 0) {
    newH = Math.max(MIN_SIZE, startH + deltaY * dy);
  }

  if (lockAspect && aspectRatio > 0) {
    if (dx === 0) {
      newW = Math.max(MIN_SIZE, newH * aspectRatio);
    } else if (dy === 0) {
      newH = Math.max(MIN_SIZE, newW / aspectRatio);
    } else {
      const wDelta = Math.abs(newW - startW);
      const hDelta = Math.abs(newH - startH);
      if (wDelta >= hDelta) {
        newH = Math.max(MIN_SIZE, newW / aspectRatio);
      } else {
        newW = Math.max(MIN_SIZE, newH * aspectRatio);
      }
    }
  }

  return { newW, newH };
};

const IMAGE_SIZE_PROPS = [
  'width',
  'min-width',
  'max-width',
  'height',
  'min-height',
  'max-height',
];

const clearResizeStyles = (el) => {
  if (!el) return;
  IMAGE_SIZE_PROPS.forEach((prop) => el.style.removeProperty(prop));
  el.style.removeProperty('flex');

  const media = el.querySelector('.hb-modal-media');
  if (media) {
    IMAGE_SIZE_PROPS.forEach((prop) => media.style.removeProperty(prop));
    media.style.removeProperty('object-fit');
  }

  const content = el.parentElement?.querySelector('.hb-modal-content');
  if (content) {
    content.style.removeProperty('flex');
    content.style.removeProperty('min-width');
  }
};

const applySize = (el, newW, newH, { important = false } = {}) => {
  if (!el) return;
  const w = `${Math.round(newW)}px`;
  const h = `${Math.round(newH)}px`;
  const priority = important ? 'important' : undefined;

  const set = (node, prop, value) => {
    if (priority) {
      node.style.setProperty(prop, value, priority);
    } else {
      node.style.setProperty(prop, value);
    }
  };

  set(el, 'width', w);
  set(el, 'min-width', w);
  set(el, 'max-width', w);
  set(el, 'height', h);
  set(el, 'min-height', h);
  set(el, 'max-height', h);
  el.style.flex = `0 0 ${w}`;

  const media = el.querySelector('.hb-modal-media');
  if (media) {
    set(media, 'width', '100%');
    set(media, 'height', h);
    set(media, 'min-height', h);
    set(media, 'max-height', h);
    media.style.objectFit = 'cover';
  }

  const content = el.parentElement?.querySelector('.hb-modal-content');
  if (content) {
    content.style.flex = '1 1 0%';
    content.style.minWidth = '0';
  }
};

export default function ImageResize({ updateSettings, settings }) {
  const uiRef = useRef(null);
  const dragState = useRef(null);
  const isDraggingRef = useRef(false);
  const selectedElRef = useRef(null);
  const updateSettingsRef = useRef(updateSettings);

  updateSettingsRef.current = updateSettings;

  // After drag, inline !important styles block Image Width/Height panel updates — sync from settings instead
  useEffect(() => {
    const hasImagePlacement =
      window.hellobarSiteSettings.modules_filename !== 'modules.js';
    if (!hasImagePlacement || isDraggingRef.current) return;

    const el = document.querySelector('.hb-modal-img');
    if (!el) return;

    const w = Number(settings.image_width);
    const h = Number(settings.image_height);
    if (!w || !h) return;

    clearResizeStyles(el);
    applySize(el, w, h);
    window.dispatchEvent(new Event('resize'));
  }, [settings.image_width, settings.image_height, settings.image_placement]);

  useEffect(() => {
    const hasImagePlacement =
      window.hellobarSiteSettings.modules_filename !== 'modules.js';
    if (!hasImagePlacement) return;

    let nodes = [];
    let outline = null;
    let toolbar = null;
    let badge = null;
    let resizeHandles = [];

    const ui = {
      get nodes() {
        return nodes;
      },
      set nodes(v) {
        nodes = v;
        uiRef.current = v;
      },
    };

    const isUiNode = (target) =>
      nodes.some((n) => n === target || n.contains?.(target));

    const removeUi = () => {
      nodes.forEach((n) => n.remove());
      nodes = [];
      ui.nodes = [];
      resizeHandles = [];
      outline = null;
      toolbar = null;
      badge = null;
      selectedElRef.current = null;
      dragState.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    const syncUiPositions = (el, sizeOverride) => {
      if (!el || !outline) return;
      const rect = el.getBoundingClientRect();
      const w = sizeOverride?.w ?? rect.width;
      const h = sizeOverride?.h ?? rect.height;

      const box = {
        top: rect.top,
        left: rect.left,
        right: rect.left + w,
        bottom: rect.top + h,
      };

      outline.style.top = `${box.top}px`;
      outline.style.left = `${box.left}px`;
      outline.style.width = `${w}px`;
      outline.style.height = `${h}px`;

      const placements = [
        { top: box.top, left: box.left },
        { top: box.top, left: box.right },
        { top: box.bottom, left: box.left },
        { top: box.bottom, left: box.right },
        { top: box.top, left: (box.left + box.right) / 2 },
        { top: box.bottom, left: (box.left + box.right) / 2 },
        { top: (box.top + box.bottom) / 2, left: box.left },
        { top: (box.top + box.bottom) / 2, left: box.right },
      ];

      resizeHandles.forEach((h, i) => {
        const { top, left } = placements[i];
        h.style.top = `${top}px`;
        h.style.left = `${left}px`;
      });

      if (toolbar) {
        const toolbarTop = Math.max(8, box.top - 40);
        toolbar.style.top = `${toolbarTop}px`;
        toolbar.style.left = `${box.left + w / 2}px`;
      }

      if (badge) {
        badge.textContent = `${Math.round(w)} × ${Math.round(h)} px`;
      }
    };

    const setDragging = (active) => {
      if (outline) {
        outline.style.borderStyle = active ? 'solid' : 'dashed';
        outline.style.borderWidth = active ? '2px' : '2px';
      }
      if (badge) badge.style.opacity = active ? '1' : '0.85';
      resizeHandles.forEach((h) => {
        h.style.pointerEvents = active ? 'none' : 'all';
      });
    };

    const bindHandle = (handle, { dx, dy, cursor }) => {
      const onEnter = () => {
        if (dragState.current) return;
        handle.style.background = BRAND;
        handle.style.transform = 'translate(-50%, -50%) scale(1.15)';
      };
      const onLeave = () => {
        if (dragState.current) return;
        handle.style.background = '#fff';
        handle.style.transform = 'translate(-50%, -50%) scale(1)';
      };

      const startDrag = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const el = selectedElRef.current;
        if (!el) return;

        const { x, y } = getPointer(e);
        const currentRect = el.getBoundingClientRect();
        dragState.current = {
          startX: x,
          startY: y,
          startW: currentRect.width,
          startH: currentRect.height,
          aspectRatio: currentRect.width / currentRect.height,
          dx,
          dy,
        };
        isDraggingRef.current = true;
        setDragging(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = cursor;
      };

      handle.addEventListener('mouseenter', onEnter);
      handle.addEventListener('mouseleave', onLeave);
      handle.addEventListener('mousedown', startDrag);
      handle.addEventListener('touchstart', startDrag, { passive: false });
    };

    const showUi = (el) => {
      removeUi();
      selectedElRef.current = el;
      const rect = el.getBoundingClientRect();
      const newNodes = [];

      outline = document.createElement('div');
      outline.style.cssText = `
        position:fixed;
        top:${rect.top}px;
        left:${rect.left}px;
        width:${rect.width}px;
        height:${rect.height}px;
        border:2px dashed ${BRAND};
        pointer-events:none;
        z-index:100000;
        border-radius:4px;
        box-sizing:border-box;
        box-shadow:0 0 0 9999px ${DIM_COLOR};
        transition:border-color 0.15s;
      `;
      document.body.appendChild(outline);
      newNodes.push(outline);

      toolbar = document.createElement('div');
      toolbar.style.cssText = `
        position:fixed;
        transform:translateX(-50%);
        display:flex;
        align-items:center;
        gap:6px;
        background:rgba(15,23,42,0.92);
        color:#fff;
        font-family:system-ui,-apple-system,sans-serif;
        font-size:11px;
        padding:5px 8px;
        border-radius:6px;
        pointer-events:none;
        z-index:100002;
        white-space:nowrap;
        box-shadow:0 4px 14px rgba(0,0,0,0.35);
        backdrop-filter:blur(4px);
      `;

      badge = document.createElement('span');
      badge.textContent = `${Math.round(rect.width)} × ${Math.round(rect.height)} px`;
      badge.style.cssText = 'font-weight:600;text-align:center;';
      toolbar.appendChild(badge);

      document.body.appendChild(toolbar);
      newNodes.push(toolbar);

      const handleDefs = [
        { dx: -1, dy: -1, cursor: 'nwse-resize', kind: 'corner' },
        { dx: 1, dy: -1, cursor: 'nesw-resize', kind: 'corner' },
        { dx: -1, dy: 1, cursor: 'nesw-resize', kind: 'corner' },
        { dx: 1, dy: 1, cursor: 'nwse-resize', kind: 'corner' },
        { dx: 0, dy: -1, cursor: 'ns-resize', kind: 'edge-h' },
        { dx: 0, dy: 1, cursor: 'ns-resize', kind: 'edge-h' },
        { dx: -1, dy: 0, cursor: 'ew-resize', kind: 'edge-v' },
        { dx: 1, dy: 0, cursor: 'ew-resize', kind: 'edge-v' },
      ];

      const cornerPositions = [
        { top: rect.top, left: rect.left },
        { top: rect.top, left: rect.right },
        { top: rect.bottom, left: rect.left },
        { top: rect.bottom, left: rect.right },
        { top: rect.top, left: (rect.left + rect.right) / 2 },
        { top: rect.bottom, left: (rect.left + rect.right) / 2 },
        { top: (rect.top + rect.bottom) / 2, left: rect.left },
        { top: (rect.top + rect.bottom) / 2, left: rect.right },
      ];

      handleDefs.forEach((def, i) => {
        const { top, left } = cornerPositions[i];
        let handle;

        if (def.kind === 'corner') {
          handle = document.createElement('div');
          Object.assign(handle.style, cornerHandleStyle(top, left, def.cursor));
        } else if (def.kind === 'edge-h') {
          handle = document.createElement('div');
          Object.assign(
            handle.style,
            edgeHandleStyle(top, left, EDGE_HANDLE_LONG, EDGE_HANDLE_SHORT, def.cursor),
          );
        } else {
          handle = document.createElement('div');
          Object.assign(
            handle.style,
            edgeHandleStyle(top, left, EDGE_HANDLE_SHORT, EDGE_HANDLE_LONG, def.cursor),
          );
        }

        bindHandle(handle, def);
        document.body.appendChild(handle);
        newNodes.push(handle);
        resizeHandles.push(handle);
      });

      el.style.outline = `2px solid transparent`;
      el.style.transition = 'outline-color 0.2s';
      el.dataset.hbResizeActive = 'true';

      ui.nodes = newNodes;
      syncUiPositions(el);
    };

    const onPointerMove = (e) => {
      if (!dragState.current) return;
      const el = selectedElRef.current;
      if (!el) return;

      const { x, y } = getPointer(e);
      const { startX, startY, startW, startH, aspectRatio, dx, dy } = dragState.current;
      const lockAspect = e.shiftKey;

      const { newW, newH } = computeSize({
        startW,
        startH,
        aspectRatio,
        deltaX: x - startX,
        deltaY: y - startY,
        dx,
        dy,
        lockAspect,
      });

      dragState.current.currentW = newW;
      dragState.current.currentH = newH;
      applySize(el, newW, newH, { important: true });
      syncUiPositions(el, { w: newW, h: newH });
    };

    const endDrag = (e) => {
      if (!dragState.current) return;
      const el = selectedElRef.current;
      if (!el) {
        dragState.current = null;
        return;
      }

      const { x, y } = getPointer(e);
      const { startX, startY, startW, startH, aspectRatio, dx, dy } = dragState.current;
      const lockAspect = e.shiftKey;

      const { newW, newH } = dragState.current.currentW != null
        ? { newW: dragState.current.currentW, newH: dragState.current.currentH }
        : computeSize({
            startW,
            startH,
            aspectRatio,
            deltaX: x - startX,
            deltaY: y - startY,
            dx,
            dy,
            lockAspect,
          });

      dragState.current = null;
      isDraggingRef.current = false;
      setDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';

      updateSettingsRef.current({
        image_width: Math.round(newW),
        image_height: Math.round(newH),
      });
      clearResizeStyles(el);
      applySize(el, newW, newH);
      syncUiPositions(el, { w: newW, h: newH });
    };

    const nudgeSize = (dw, dh) => {
      const el = selectedElRef.current;
      if (!el || !outline) return;

      const rect = el.getBoundingClientRect();
      let newW = Math.max(MIN_SIZE, rect.width + dw);
      let newH = Math.max(MIN_SIZE, rect.height + dh);

      updateSettingsRef.current({
        image_width: Math.round(newW),
        image_height: Math.round(newH),
      });
      clearResizeStyles(el);
      applySize(el, newW, newH);
      syncUiPositions(el, { w: newW, h: newH });
    };

    const onKeyDown = (e) => {
      if (!selectedElRef.current || !outline) return;

      const step = e.shiftKey ? 10 : 1;
      switch (e.key) {
        case 'Escape':
          removeUi();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nudgeSize(step, 0);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          nudgeSize(-step, 0);
          break;
        case 'ArrowDown':
          e.preventDefault();
          nudgeSize(0, step);
          break;
        case 'ArrowUp':
          e.preventDefault();
          nudgeSize(0, -step);
          break;
        default:
          break;
      }
    };

    const onDocClick = (e) => {
      const el = document.querySelector('.hb-modal-img');
      if (!el) {
        removeUi();
        return;
      }

      if (el === e.target || el.contains(e.target)) {
        showUi(el);
        return;
      }

      if (!isUiNode(e.target)) {
        removeUi();
      }
    };

    const refreshIfSelected = () => {
      const el = selectedElRef.current || document.querySelector('.hb-modal-img');
      if (el && outline) {
        syncUiPositions(el);
      }
    };

    document.addEventListener('click', onDocClick, true);
    document.addEventListener('mousemove', onPointerMove);
    document.addEventListener('mouseup', endDrag);
    document.addEventListener('touchmove', onPointerMove, { passive: false });
    document.addEventListener('touchend', endDrag);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', refreshIfSelected);
    window.addEventListener('scroll', refreshIfSelected, true);

    return () => {
      isDraggingRef.current = false;
      const active = document.querySelector('.hb-modal-img[data-hb-resize-active]');
      if (active) {
        delete active.dataset.hbResizeActive;
        active.style.outline = '';
        active.style.transition = '';
        clearResizeStyles(active);
      }
      removeUi();
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchmove', onPointerMove);
      document.removeEventListener('touchend', endDrag);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', refreshIfSelected);
      window.removeEventListener('scroll', refreshIfSelected, true);
    };
  }, [settings.image_placement]);

  return null;
}
